import { Edit3, Heart, Save, Trash2, Upload, X } from 'lucide-react'
import { Modal } from '../../../components/Modal'
import { Button } from '../../../components/ui/Button'
import { formatBytes } from '../utils/galleryStore'
import { avatarUrlForUser } from '../utils/avatar'
import { ImageCropper } from './ImageCropper'

export function PinDetailsContent({
  open,
  onClose,
  edit,
  setEdit,
  busy,
  onToggleLike,
  pin,
  userId,
  liked,
  likes,
  replaceInputRef,
  doReplaceMedia,
  onDelete,
  doSave,
  isVideo,
  mediaUrl,
  thumbUrl,
  title,
  setTitle,
  visibility,
  setVisibility,
  tags,
  removeTag,
  tagInput,
  setTagInput,
  addTag,
  suggestedTags,
  cropperRef,
  maxSize,
  setMaxSize,
  quality,
  setQuality,
  videoRef,
  setVideoDuration,
  trimStart,
  setTrimStart,
  trimEnd,
  setTrimEnd,
  videoDuration,
  doTrimVideo,
  stopTrimPreview,
}) {
  const p = pin
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={edit ? 'Edit pin' : 'Pin'}
      footer={
        <div className="w-full flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Button
              variant="neutral"
              onClick={() => onToggleLike?.(p?.id)}
              disabled={!userId}
              title={userId ? 'Like' : '请先登录后再点赞'}
            >
              <Heart className={`w-4 h-4 ${liked ? 'fill-brand-pink text-brand-pink' : ''}`} />
              {likes.length || 0}
            </Button>

            <Button variant="neutral" onClick={() => replaceInputRef.current?.click?.()} disabled={busy}>
              <Upload className="w-4 h-4" />
              Replace
            </Button>
            <input
              ref={replaceInputRef}
              type="file"
              accept="image/*,video/*"
              className="hidden"
              onChange={(e) => doReplaceMedia(e.target.files?.[0] || null)}
            />
          </div>

          <div className="flex items-center gap-2">
            {!edit ? (
              <Button variant="secondary" onClick={() => setEdit(true)} disabled={busy}>
                <Edit3 className="w-4 h-4" />
                Edit
              </Button>
            ) : (
              <Button variant="secondary" onClick={doSave} disabled={busy}>
                <Save className="w-4 h-4" />
                Save
              </Button>
            )}

            <Button variant="neutral" onClick={() => setEdit(false)} disabled={!edit || busy}>
              <X className="w-4 h-4" />
              Cancel
            </Button>

            <Button variant="danger" onClick={() => onDelete?.(p?.id)} disabled={busy}>
              <Trash2 className="w-4 h-4" />
              Delete
            </Button>
          </div>
        </div>
      }
    >
      {!p ? null : (
        <div className="space-y-4">
          {!edit ? (
            <div className="space-y-3">
              <div className="w-full rounded-3xl border-2 border-brand-textDark overflow-hidden bg-black/5">
                {isVideo ? (
                  <video
                    src={mediaUrl}
                    poster={thumbUrl}
                    controls
                    className="w-full max-h-[60vh] object-contain bg-black"
                  />
                ) : (
                  <img src={mediaUrl} alt={p.title} className="w-full max-h-[60vh] object-contain bg-black/5" />
                )}
              </div>

              <div className="space-y-2">
                <div className="text-base font-black">{p.title}</div>
                <div className="flex flex-wrap items-center gap-2 text-xs font-bold text-brand-text/70">
                  <span className="px-2 py-1 rounded-full border-2 border-brand-textDark/15 bg-brand-bg">
                    {p.visibility === 'public' ? 'Public' : 'Private'}
                  </span>
                  <span className="px-2 py-1 rounded-full border-2 border-brand-textDark/15 bg-brand-bg">
                    {p.media.kind.toUpperCase()}
                  </span>
                  <span className="px-2 py-1 rounded-full border-2 border-brand-textDark/15 bg-brand-bg">
                    {formatBytes(p.media.sizeBytes || 0)}
                  </span>
                  {p.media.durationMs ? (
                    <span className="px-2 py-1 rounded-full border-2 border-brand-textDark/15 bg-brand-bg">
                      {(p.media.durationMs / 1000).toFixed(1)}s
                    </span>
                  ) : null}
                </div>
              </div>

              <div className="space-y-2">
                <div className="text-xs font-black tracking-widest uppercase text-brand-text/60">Tags</div>
                <div className="flex flex-wrap gap-2">
                  {(p.tags || []).length ? (
                    p.tags.map((t) => (
                      <span key={t} className="px-2.5 py-1 rounded-full border-2 border-brand-textDark/10 bg-white text-xs font-extrabold">
                        #{t}
                      </span>
                    ))
                  ) : (
                    <div className="text-sm font-bold text-brand-text/70">No tags</div>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <div className="text-xs font-black tracking-widest uppercase text-brand-text/60">Likes</div>
                {likes.length ? (
                  <div className="flex items-center -space-x-2">
                    {likes.slice(0, 12).map((id) => (
                      <img
                        key={id}
                        src={avatarUrlForUser({ id })}
                        alt={id}
                        className="w-9 h-9 rounded-full border-2 border-white bg-white object-cover"
                        title={id}
                      />
                    ))}
                    {likes.length > 12 ? (
                      <div className="w-9 h-9 rounded-full border-2 border-white bg-brand-yellow flex items-center justify-center text-xs font-black">
                        +{likes.length - 12}
                      </div>
                    ) : null}
                  </div>
                ) : (
                  <div className="text-sm font-bold text-brand-text/70">No likes yet</div>
                )}
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="space-y-2">
                <div className="text-xs font-black tracking-widest uppercase text-brand-text/60">Title</div>
                <input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Title"
                  className="w-full rounded-3xl border-2 border-brand-textDark/20 dark:border-slate-700 bg-white dark:bg-slate-950 px-4 py-3 text-sm font-extrabold outline-none focus:border-brand-cyan focus:ring-2 focus:ring-brand-cyan/20"
                />
              </div>

              <div className="space-y-2">
                <div className="text-xs font-black tracking-widest uppercase text-brand-text/60">Visibility</div>
                <div className="flex items-center gap-2">
                  <Button
                    variant={visibility === 'private' ? 'secondary' : 'neutral'}
                    onClick={() => setVisibility('private')}
                    disabled={busy}
                  >
                    Private
                  </Button>
                  <Button
                    variant={visibility === 'public' ? 'secondary' : 'neutral'}
                    onClick={() => setVisibility('public')}
                    disabled={busy}
                  >
                    Public
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                <div className="text-xs font-black tracking-widest uppercase text-brand-text/60">Tags</div>
                <div className="flex flex-wrap gap-2">
                  {tags.map((t) => (
                    <button
                      type="button"
                      key={t}
                      className="px-2.5 py-1 rounded-full border-2 border-brand-textDark/10 bg-white text-xs font-extrabold flex items-center gap-1"
                      onClick={() => removeTag(t)}
                      title="Remove"
                    >
                      <span>#{t}</span>
                      <span className="text-brand-text/60">×</span>
                    </button>
                  ))}
                </div>
                <input
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  placeholder="Add tag (press Enter)…"
                  className="w-full rounded-3xl border-2 border-brand-textDark/20 dark:border-slate-700 bg-white dark:bg-slate-950 px-4 py-3 text-sm font-extrabold outline-none focus:border-brand-cyan focus:ring-2 focus:ring-brand-cyan/20"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      addTag(tagInput)
                    }
                  }}
                />
                {suggestedTags.length ? (
                  <div className="flex flex-wrap gap-2">
                    {suggestedTags.map((t) => (
                      <button
                        type="button"
                        key={t}
                        className="px-2.5 py-1 rounded-full border-2 border-brand-textDark/10 bg-brand-bg text-xs font-extrabold"
                        onClick={() => addTag(t)}
                      >
                        #{t}
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>

              {!isVideo ? (
                <div className="space-y-3">
                  <div className="text-xs font-black tracking-widest uppercase text-brand-text/60">Media preview (crop)</div>
                  <ImageCropper ref={cropperRef} src={mediaUrl} />
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <div className="text-xs font-black text-brand-text/60">Max size</div>
                      <select
                        value={String(maxSize)}
                        onChange={(e) => setMaxSize(Number(e.target.value) || 1080)}
                        className="w-full rounded-2xl border-2 border-brand-textDark/20 bg-white px-3 py-2 text-sm font-extrabold"
                      >
                        <option value="720">720</option>
                        <option value="1080">1080</option>
                        <option value="1440">1440</option>
                      </select>
                    </div>
                    <div className="space-y-1">
                      <div className="text-xs font-black text-brand-text/60">Quality</div>
                      <input
                        type="range"
                        min={0.4}
                        max={0.98}
                        step={0.01}
                        value={quality}
                        onChange={(e) => setQuality(Number(e.target.value))}
                        className="w-full accent-brand-pink"
                      />
                      <div className="text-xs font-mono text-brand-text/70 text-right">{quality.toFixed(2)}</div>
                    </div>
                  </div>
                  <div className="text-xs font-bold text-brand-text/70">
                    说明：这里会把图片裁剪并压缩后再保存（同时会展示文件大小）。
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="text-xs font-black tracking-widest uppercase text-brand-text/60">Video</div>
                  <div className="w-full rounded-3xl border-2 border-brand-textDark overflow-hidden bg-black">
                    <video
                      ref={videoRef}
                      src={mediaUrl}
                      poster={thumbUrl}
                      controls
                      className="w-full max-h-[50vh] object-contain"
                      onLoadedMetadata={(e) => {
                        const dur = Number(e.currentTarget?.duration) || 0
                        if (!Number.isFinite(dur) || dur <= 0) return
                        setVideoDuration(dur)
                        setTrimStart(0)
                        setTrimEnd(dur)
                      }}
                    />
                  </div>
                  <div className="space-y-3">
                    <div className="text-xs font-black tracking-widest uppercase text-brand-text/60">Trim</div>
                    <div className="rounded-3xl border-2 border-brand-textDark/10 bg-brand-bg px-4 py-4 space-y-3">
                      <div className="text-xs font-bold text-brand-text/70">
                        {videoDuration ? `Start ${trimStart.toFixed(2)}s · End ${trimEnd.toFixed(2)}s · New ${(trimEnd - trimStart).toFixed(2)}s` : 'Load video metadata to enable trim.'}
                      </div>

                      <div className="space-y-2">
                        <div className="flex items-center justify-between text-xs font-black text-brand-text/60">
                          <span>Start</span>
                          <span className="font-mono">{trimStart.toFixed(2)}s</span>
                        </div>
                        <input
                          type="range"
                          min={0}
                          max={Math.max(0, videoDuration - 0.05)}
                          step={0.05}
                          value={trimStart}
                          disabled={!videoDuration || busy}
                          onChange={(e) => {
                            const v = Math.max(0, Math.min(Number(e.target.value) || 0, Math.max(0, trimEnd - 0.05)))
                            setTrimStart(v)
                          }}
                          className="w-full accent-brand-pink"
                        />
                      </div>

                      <div className="space-y-2">
                        <div className="flex items-center justify-between text-xs font-black text-brand-text/60">
                          <span>End</span>
                          <span className="font-mono">{trimEnd.toFixed(2)}s</span>
                        </div>
                        <input
                          type="range"
                          min={Math.min(videoDuration || 0, trimStart + 0.05)}
                          max={videoDuration || 0}
                          step={0.05}
                          value={trimEnd}
                          disabled={!videoDuration || busy}
                          onChange={(e) => {
                            const v = Math.max(trimStart + 0.05, Math.min(Number(e.target.value) || 0, videoDuration || 0))
                            setTrimEnd(v)
                          }}
                          className="w-full accent-brand-pink"
                        />
                      </div>

                      <div className="flex items-center gap-2">
                        <Button
                          variant="secondary"
                          onClick={doTrimVideo}
                          disabled={busy || !videoDuration || trimEnd <= trimStart + 0.05}
                        >
                          Trim & replace
                        </Button>
                        <Button variant="neutral" onClick={stopTrimPreview} disabled={!busy}>
                          Stop
                        </Button>
                      </div>
                      <div className="text-xs font-bold text-brand-text/70">
                        说明：Web 预览版会在本地导出一个新视频（通常是 WebM），并替换当前视频。
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </Modal>
  )
}
